import { useState } from 'react';

/**
 * Range comparison — two spans on a shared scale.
 * Emphasis form: accent for us, de-emphasis gray for the field.
 * Accent #6366f1 validated against the dark surface (#0f172a);
 * separation from #64748b: CVD ΔE 16.8, normal-vision ΔE 16.9.
 */

interface Span {
  label: string;
  sub: string;
  min: number;
  max: number;
  display: string;
  accent?: boolean;
}

const spans: Span[] = [
  {
    label: 'Others',
    sub: 'published list rates across the category',
    min: 0.5,
    max: 2.0,
    display: '$0.50 – $2.00',
  },
  {
    label: 'DialogMe',
    sub: 'effective rate at plan limit',
    min: 0.09,
    max: 0.16,
    display: '€0.09 – €0.16',
    accent: true,
  },
];

const AXIS_MAX = 2.0;
const ACCENT = '#6366f1';
const CONTEXT = '#64748b';

export function CostComparisonChart() {
  const [hovered, setHovered] = useState<string | null>(null);
  const pct = (v: number) => (v / AXIS_MAX) * 100;

  return (
    <figure className="m-0">
      {/* axis */}
      <div className="relative h-5 mb-3">
        {[0, 0.5, 1.0, 1.5, 2.0].map((t) => (
          <div key={t} className="absolute top-0 -translate-x-1/2" style={{ left: `${pct(t)}%` }}>
            <span className="text-[10px] text-slate-600 tabular-nums">
              {t === 0 ? '0' : t.toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        {spans.map((span) => {
          const isHovered = hovered === span.label;
          return (
            <div
              key={span.label}
              className="cursor-default"
              onMouseEnter={() => setHovered(span.label)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(span.label)}
              onBlur={() => setHovered(null)}
              tabIndex={0}
            >
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <span
                  className={`text-sm font-semibold transition-colors ${
                    span.accent ? 'text-slate-100' : 'text-slate-400'
                  }`}
                >
                  {span.label}
                </span>
                <span
                  className={`text-sm font-mono tabular-nums transition-colors ${
                    span.accent ? 'text-indigo-300' : 'text-slate-500'
                  }`}
                >
                  {span.display}
                </span>
              </div>

              {/* track with range span */}
              <div className="relative h-4 rounded-sm bg-slate-800/60">
                {/* gridlines */}
                {[0.5, 1.0, 1.5].map((t) => (
                  <div
                    key={t}
                    className="absolute inset-y-0 w-px bg-slate-700/40"
                    style={{ left: `${pct(t)}%` }}
                    aria-hidden="true"
                  />
                ))}

                {/* span */}
                <div
                  className="absolute inset-y-1 rounded-[3px] transition-opacity duration-200"
                  style={{
                    left: `${pct(span.min)}%`,
                    width: `${Math.max(pct(span.max - span.min), 1.2)}%`,
                    background: span.accent ? ACCENT : CONTEXT,
                    opacity: hovered && !isHovered ? 0.5 : 1,
                  }}
                />
                {/* end caps */}
                {[span.min, span.max].map((v) => (
                  <div
                    key={v}
                    className="absolute inset-y-0 w-[3px] rounded-full"
                    style={{
                      left: `${pct(v)}%`,
                      background: span.accent ? ACCENT : CONTEXT,
                      opacity: hovered && !isHovered ? 0.5 : 1,
                    }}
                  />
                ))}
              </div>

              <p
                className={`text-[11px] mt-1.5 transition-colors ${
                  isHovered ? 'text-slate-400' : 'text-slate-600'
                }`}
              >
                {span.sub}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-5 pt-4 border-t border-slate-700/40">
        <p className="text-[13px] text-slate-300 leading-relaxed">
          <span className="text-white font-semibold">Our entire range sits below where the field
          begins.</span>{' '}
          The gap is structural, not promotional — DialogMe runs on its own AI stack, Fnk,
          itself rather than paying a model vendor per token, and abandoned conversations release
          their compute instead of finishing unwatched.
        </p>
        <p className="text-[11px] text-slate-500 leading-relaxed mt-2">
          Published list rates, 2026. The category prices per resolved conversation; our figure is
          the effective rate at plan limit, so real accounts will land higher. Rates shown in the
          currency each is published in.
        </p>
      </div>
    </figure>
  );
}
